import React, { useState, useEffect } from 'react';
import { customerAPI, subscriberAPI } from '../services/api';
import DataTable from '../components/ui/DataTable';
import toast from 'react-hot-toast';
import { Plus, Edit2, Trash2, Users, Eye, X, Save, Store, Globe, Mail, MailX, BadgeCheck, Download, Inbox } from 'lucide-react';

const EMPTY_FORM = {
  customerName: '', mobile: '', phone: '', email: '',
  address: '', city: '', state: '', pinCode: '',
  gstNumber: '', panNumber: '', creditLimit: 0, creditDays: 0,
  openingBalance: 0,
};

export default function CustomersPage() {
  const [data, setData]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal]     = useState(null); // null | 'add' | 'edit'
  const [form, setForm]       = useState(EMPTY_FORM);
  const [saving, setSaving]   = useState(false);
  const [editID, setEditID]   = useState(null);
  // '' = everyone, otherwise the source column: showroom walk-ins or website sign-ups
  const [tab, setTab]         = useState('');
  const [counts, setCounts]   = useState({ all: 0, showroom: 0, website: 0, subscribers: 0 });
  // People who only ever gave an email address. They are not customers — no
  // name, no phone, nothing bought — so they live in their own table and this
  // tab reads from it rather than filtering the customer list.
  const [subscribers, setSubscribers] = useState([]);
  // Which kind of customer the open form belongs to; a website shopper has no
  // GST, PAN or credit terms, so those fields are left out for them
  const [formSource, setFormSource] = useState('showroom');

  const loadSubscribers = async () => {
    setLoading(true);
    try {
      const res = await subscriberAPI.getAll();
      const rows = res.data.data || [];
      setSubscribers(rows);
      setCounts((c) => ({ ...c, subscribers: rows.length }));
    } catch { toast.error('Failed to load the mailing list'); }
    finally { setLoading(false); }
  };

  const load = async (which = tab) => {
    if (which === 'subscribers') return loadSubscribers();
    setLoading(true);
    try {
      const res = await customerAPI.getAll({ isActive: true, source: which || undefined });
      const rows = res.data.data || [];
      setData(rows);

      // Tab badges count the whole table, not the filtered view, so the totals
      // are only recalculated when the unfiltered list comes back
      if (!which) {
        setCounts({
          all: rows.length,
          showroom: rows.filter(r => (r.source || 'showroom') !== 'website').length,
          website:  rows.filter(r => r.source === 'website').length,
        });
      }
    } catch { toast.error('Failed to load customers'); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load('');                 // counts need the unfiltered list first
    subscriberAPI.getAll()
      .then((res) => setCounts((c) => ({ ...c, subscribers: (res.data.data || []).length })))
      .catch(() => {});       // a badge is not worth an error message
  }, []);

  const switchTab = (next) => { setTab(next); load(next); };

  const downloadCsv = async () => {
    try {
      const res = await subscriberAPI.exportCsv();
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `carat-quest-subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error('Could not download the list'); }
  };

  const openAdd = () => { setForm(EMPTY_FORM); setEditID(null); setFormSource('showroom'); setModal('form'); };

  const openEdit = (row) => {
    setForm({
      customerName: row.customerName, mobile: row.mobile || '',
      phone: row.phone || '', email: row.email || '',
      address: row.address || '', city: row.city || '',
      state: row.state || '', pinCode: row.pinCode || '',
      gstNumber: row.gstNumber || '', panNumber: row.panNumber || '',
      creditLimit: row.creditLimit || 0, creditDays: row.creditDays || 0,
      openingBalance: row.openingBalance || 0,
    });
    setEditID(row.customerID);
    setFormSource(row.source || 'showroom');
    setModal('form');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.customerName) return toast.error('Customer name required');
    setSaving(true);
    try {
      if (editID) {
        await customerAPI.update(editID, { ...form, isActive: true });
        toast.success('Customer updated');
      } else {
        await customerAPI.create(form);
        toast.success('Customer created');
      }
      setModal(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Deactivate "${name}"?`)) return;
    try {
      await customerAPI.delete(id);
      toast.success('Customer deactivated');
      load();
    } catch { toast.error('Failed to deactivate'); }
  };

  const dash = <span className="text-muted">—</span>;
  const asDate = (v) => v ? new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : dash;

  const codeCol = { header: 'Code', key: 'customerCode', width: 100,
    render: r => <code className="text-mono" style={{ fontSize: 12 }}>{r.customerCode}</code> };

  const nameCol = { header: 'Name', key: 'customerName', render: r => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      {r.avatarUrl && (
        <img src={r.avatarUrl} alt="" style={{ width: 26, height: 26, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
      )}
      <span style={{ fontWeight: 600 }}>{r.customerName}</span>
      {tab === '' && r.source === 'website' && (
        <span className="badge badge-blue" style={{ fontSize: 10 }}>Website</span>
      )}
    </div>
  )};

  const statusCol = { header: 'Status', key: 'isActive', align: 'center',
    render: r => <span className={`badge ${r.isActive ? 'badge-green' : 'badge-red'}`}>{r.isActive ? 'Active' : 'Inactive'}</span> };

  const actionsCol = { header: 'Actions', align: 'center', render: r => (
    <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
      <button className="btn btn-ghost btn-sm btn-icon" onClick={() => openEdit(r)} title="Edit"><Edit2 size={14} /></button>
      <button className="btn btn-ghost btn-sm btn-icon" onClick={() => handleDelete(r.customerID, r.customerName)} title="Deactivate" style={{ color: 'var(--red)' }}><Trash2 size={14} /></button>
    </div>
  )};

  // Showroom rows carry GST and credit terms. Website shoppers have neither —
  // what matters for them is the email, whether it is reachable, and whether
  // they agreed to be marketed to.
  const showroomColumns = [
    codeCol,
    nameCol,
    { header: 'Mobile',  key: 'mobile', render: r => r.mobile || dash },
    { header: 'City',    key: 'city',   render: r => r.city || dash },
    { header: 'GST No.', key: 'gstNumber', render: r => r.gstNumber || dash },
    { header: 'Credit Limit', key: 'creditLimit', align: 'right',
      render: r => <span className="text-mono">₹{Number(r.creditLimit||0).toLocaleString('en-IN')}</span> },
    statusCol,
    actionsCol,
  ];

  const websiteColumns = [
    codeCol,
    nameCol,
    { header: 'Email', key: 'email', render: r => r.email ? (
        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          {r.email}
          {r.emailVerified && <BadgeCheck size={13} style={{ color: 'var(--green)', flexShrink: 0 }} />}
        </span>
      ) : dash },
    // Google never sends a phone number — this fills in at the first order,
    // where the delivery address makes it compulsory
    { header: 'Mobile', key: 'mobile',
      render: r => r.mobile || <span className="text-muted" title="Google does not share a phone number — collected at the first order">—</span> },
    { header: 'Signed up',  key: 'createdOn',   render: r => asDate(r.createdOn) },
    { header: 'Last login', key: 'lastLoginOn', render: r => asDate(r.lastLoginOn) },
    { header: 'Marketing', key: 'marketingConsent', align: 'center', render: r => r.marketingConsent
      ? <span className="badge badge-green" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Mail size={11} /> Opted in</span>
      : <span className="badge badge-gray" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><MailX size={11} /> No consent</span> },
    statusCol,
    actionsCol,
  ];

  const subscriberColumns = [
    { key: 'email', label: 'Email', render: (r) => (
        <span className="text-mono" style={{ fontSize: 12.5 }}>{r.email}</span>
      ) },
    { key: 'name', label: 'Name', render: (r) => r.name || <span className="text-muted">—</span> },
    { key: 'source', label: 'Signed up on', render: (r) => (
        <span className="text-muted" style={{ fontSize: 12 }}>{r.source || 'website'}</span>
      ) },
    { key: 'customerId', label: 'Account', render: (r) => (
        r.customerId
          ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 700, color: '#15803d' }}>
              <BadgeCheck size={13} /> Signed in{r.orderCount > 0 ? ` · ${r.orderCount} order${r.orderCount > 1 ? 's' : ''}` : ''}
            </span>
          : <span className="text-muted" style={{ fontSize: 12 }}>Email only</span>
      ) },
    { key: 'subscribedOn', label: 'Joined', render: (r) => (
        <span className="text-muted" style={{ fontSize: 12 }}>
          {new Date(r.subscribedOn).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
        </span>
      ) },
  ];

  const columns = tab === 'subscribers' ? subscriberColumns
                : tab === 'website' ? websiteColumns
                : showroomColumns;

  const TABS = [
    { key: '',         label: 'All',      icon: Users, count: counts.all },
    { key: 'showroom', label: 'Showroom', icon: Store, count: counts.showroom },
    { key: 'website',  label: 'Website',  icon: Globe, count: counts.website },
    { key: 'subscribers', label: 'Mailing List', icon: Mail, count: counts.subscribers },
  ];

  return (
    <div>
      <div className="flex-between mb-6">
        <div>
          <h1>Customers</h1>
          <p className="text-muted">
            {tab === 'subscribers'
              ? `${subscribers.length} on the mailing list · ${subscribers.filter(r => r.customerId).length} of them have an account`
              : tab === 'website'
                ? `${data.length} website ${data.length === 1 ? 'shopper' : 'shoppers'} · ${data.filter(r => r.marketingConsent).length} opted in to email`
                : `${data.length} active ${data.length === 1 ? 'customer' : 'customers'}`}
          </p>
        </div>
        {tab === 'subscribers' ? (
          <button className="btn btn-amber" onClick={downloadCsv}>
            <Download size={16} /> Download CSV
          </button>
        ) : (
          <button className="btn btn-amber" onClick={openAdd}>
            <Plus size={16} /> Add Customer
          </button>
        )}
      </div>

      {/* Showroom and website customers share one table, so the tabs are a
          filter on the same list rather than separate screens */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
        {TABS.map(t => {
          const Icon = t.icon;
          const on = tab === t.key;
          return (
            <button
              key={t.key || 'all'}
              type="button"
              onClick={() => switchTab(t.key)}
              className={`btn btn-sm ${on ? 'btn-primary' : 'btn-ghost'}`}
              style={{ display: 'flex', alignItems: 'center', gap: 7 }}
            >
              <Icon size={14} />
              {t.label}
              <span
                className="text-mono"
                style={{
                  fontSize: 11, padding: '1px 6px', borderRadius: 10,
                  background: on ? 'rgba(255,255,255,0.22)' : 'var(--bg-subtle, #eef2f6)',
                  color: on ? '#fff' : 'var(--text-muted)',
                }}
              >
                {t.count}
              </span>
            </button>
          );
        })}
      </div>

      {tab === 'subscribers' && (
        <div
          className="mb-4"
          style={{
            display: 'flex', alignItems: 'flex-start', gap: 9, padding: '11px 14px',
            background: 'var(--bg-subtle, #eff6ff)', border: '1px solid var(--border, #dbeafe)',
            borderRadius: 8, fontSize: 12.5, lineHeight: 1.55,
          }}
        >
          <Mail size={15} style={{ flexShrink: 0, marginTop: 2, color: 'var(--blue, #2563eb)' }} />
          <span className="text-muted">
            Everyone who entered their address in a <strong>newsletter form</strong> on the website. Most have
            never bought anything, so they are kept apart from customers. <strong>Download CSV</strong> gives you
            the list in the form Mailchimp and Brevo expect.
          </span>
        </div>
      )}

      {tab === 'website' && (
        <div
          className="mb-4"
          style={{
            display: 'flex', alignItems: 'flex-start', gap: 9, padding: '11px 14px',
            background: 'var(--bg-subtle, #eff6ff)', border: '1px solid var(--border, #dbeafe)',
            borderRadius: 8, fontSize: 12.5, lineHeight: 1.55,
          }}
        >
          <Globe size={15} style={{ flexShrink: 0, marginTop: 2, color: 'var(--blue, #2563eb)' }} />
          <span className="text-muted">
            Shoppers who signed in on <strong>thecaratquest.com</strong>. Google supplies the name, email and photo
            but never a phone number, so mobile fills in at their first order.
            Only rows marked <strong>Opted in</strong> may be sent marketing email.
          </span>
        </div>
      )}

      <div className="card">
        <div className="card-body" style={{ padding: 0 }}>
          <div style={{ padding: '16px 20px' }}>
            <DataTable
              data={tab === 'subscribers' ? subscribers : data}
              columns={columns}
              loading={loading}
              searchKeys={tab === 'subscribers'
                ? ['email', 'name', 'source']
                : ['customerName', 'mobile', 'email', 'customerCode', 'city', 'gstNumber']}
              emptyIcon={tab === 'subscribers' ? <Inbox size={36} />
                       : tab === 'website' ? <Globe size={36} /> : <Users size={36} />}
              emptyText={tab === 'subscribers'
                ? 'Nobody has signed up yet — addresses from the website newsletter forms land here'
                : tab === 'website'
                  ? 'No website sign-ups yet — they appear here as soon as customer login goes live'
                  : 'No customers yet — add your first one'}
            />
          </div>
        </div>
      </div>

      {/* Form Modal */}
      {modal === 'form' && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(null)}>
          <div className="modal" style={{ maxWidth: 640 }}>
            <div className="modal-header">
              <h3>{editID ? 'Edit Customer' : 'Add Customer'}</h3>
              {formSource === 'website' && (
                <span className="badge badge-blue" style={{ marginLeft: 10, fontSize: 10 }}>Website</span>
              )}
              <button className="btn btn-ghost btn-icon" onClick={() => setModal(null)}><X size={18} /></button>
            </div>
            <form onSubmit={handleSave}>
              <div className="modal-body">
                <div className="grid-2" style={{ gap: 14 }}>
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label className="form-label">Customer Name *</label>
                    <input className="form-control" value={form.customerName}
                      onChange={e => setForm({ ...form, customerName: e.target.value })}
                      placeholder="Full name" required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Mobile</label>
                    <input className="form-control" value={form.mobile}
                      onChange={e => setForm({ ...form, mobile: e.target.value })}
                      placeholder="9876543210" maxLength={15} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Email</label>
                    <input className="form-control" type="email" value={form.email}
                      onChange={e => setForm({ ...form, email: e.target.value })}
                      placeholder="email@example.com" />
                  </div>
                  {formSource !== 'website' && (
                    <>
                      <div className="form-group">
                        <label className="form-label">GST Number</label>
                        <input className="form-control" value={form.gstNumber}
                          onChange={e => setForm({ ...form, gstNumber: e.target.value.toUpperCase() })}
                          placeholder="27XXXXX0000X1Z5" maxLength={20} />
                      </div>
                      <div className="form-group">
                        <label className="form-label">PAN Number</label>
                        <input className="form-control" value={form.panNumber}
                          onChange={e => setForm({ ...form, panNumber: e.target.value.toUpperCase() })}
                          placeholder="XXXXX0000X" maxLength={10} />
                      </div>
                    </>
                  )}
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label className="form-label">Address</label>
                    <input className="form-control" value={form.address}
                      onChange={e => setForm({ ...form, address: e.target.value })}
                      placeholder="Street address" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">City</label>
                    <input className="form-control" value={form.city}
                      onChange={e => setForm({ ...form, city: e.target.value })}
                      placeholder="Ahmedabad" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">State</label>
                    <input className="form-control" value={form.state}
                      onChange={e => setForm({ ...form, state: e.target.value })}
                      placeholder="Gujarat" />
                  </div>
                  {formSource !== 'website' && (
                    <>
                      <div className="form-group">
                        <label className="form-label">Credit Limit (₹)</label>
                        <input className="form-control" type="number" min="0" value={form.creditLimit}
                          onChange={e => setForm({ ...form, creditLimit: e.target.value })} />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Credit Days</label>
                        <input className="form-control" type="number" min="0" value={form.creditDays}
                          onChange={e => setForm({ ...form, creditDays: e.target.value })} />
                      </div>
                    </>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-ghost" onClick={() => setModal(null)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? <span className="spinner" style={{ width: 16, height: 16 }} /> : <Save size={15} />}
                  {saving ? 'Saving...' : editID ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
